import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ACCOUNT_TYPE_STYLES } from "../../../Common/TreeView";
import { ChartOfAccountStyles } from "./ChartOfAccountShared";

const emptyInputs = {
  parent_id: "",
  code: "",
  name: "",
  account_type: "",
  opening_amount: "",
  trial_balance_dr_cr: "",
  trial_opening_balance: "",
  description: "",
};
// The picker holds the whole active chart. On the edit form the account's own
// sub-tree comes back flagged isDisabled rather than missing, so the list still
// visibly contains every account.
const formatParentOption = (option) => (
  <div className="d-flex justify-content-between align-items-center">
    <span>{option.label}</span>
    <small className="text-muted ms-2">
      {option.disabled_reason || option.account_type || ""}
    </small>
  </div>
);

const Create = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState(emptyInputs);
  const [parents, setParents] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [parentRes, typeRes, codeRes] = await Promise.all([
          Http.get("/chart-of-account-parent-options"),
          Http.get("/chart-of-account-type-options"),
          Http.get("/chart-of-account-code-last-code/0"),
        ]);

        setParents(parentRes.data || []);
        setAccountTypes(typeRes.data || []);
        setInputs((values) => ({ ...values, code: String(codeRes.data ?? "") }));
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: ["Could not load the form data. Please refresh and try again."],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // react-select hands back null when the picker is cleared, which the old
  // handler dereferenced straight into a crash.
  const parentHandleChange = (selectedOption) => {
    setSelectedParent(selectedOption);

    setInputs((values) => ({
      ...values,
      parent_id: selectedOption ? selectedOption.value : "",
      // A sub-account belongs to the same statement class as its parent; still
      // editable afterwards.
      account_type:
        selectedOption && selectedOption.account_type
          ? selectedOption.account_type
          : values.account_type,
    }));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }
    if (!inputs.account_type) {
      errors.account_type = ["Account type is required."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.post("/chart-of-account-code", {
      ...inputs,
      parent_id: inputs.parent_id || null,
      opening_amount: inputs.opening_amount === "" ? 0 : inputs.opening_amount,
    })
      .then((res) => {
        navigate("/chart-of-account", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        // A 500 or a dropped connection has no `errors` payload; showing the
        // message beats throwing inside the promise and leaving a dead button.
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not save the account. Please try again.",
            ],
          }
        );
      })
      .finally(() => setSaving(false));
  };

  const typeStyle = ACCOUNT_TYPE_STYLES[inputs.account_type];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Create Chart of Account</h5>
            <p className="text-muted mb-0 tx-12">
              Add a new account to the hierarchy
            </p>
          </div>
          <Link to="/chart-of-account" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="coa-panel">
                <div className="coa-panel-head">
                  <span>
                    <i className="fa fa-sitemap me-2"></i>Account Identity
                  </span>
                  {typeStyle && (
                    <span
                      className="coa-pill"
                      style={{ background: typeStyle.bg, color: "#fff" }}
                    >
                      {typeStyle.label}
                    </span>
                  )}
                </div>
                <div className="coa-panel-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <label>Parent Account</label>
                      <Select
                        className="basic-single"
                        classNamePrefix="select"
                        placeholder="No parent (root account)"
                        name="parent_id"
                        options={parents}
                        value={selectedParent}
                        onChange={parentHandleChange}
                        formatOptionLabel={formatParentOption}
                        isOptionDisabled={(option) => option.isDisabled}
                        maxMenuHeight={320}
                        isClearable
                        isSearchable
                      />
                      <small className="coa-hint">
                        {parents.length} active accounts. Leave empty to create
                        a top-level account.
                      </small>
                    </div>
                    <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                      <label>
                        Code <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="text"
                        className="form-control coa-mono"
                        name="code"
                        value={inputs.code}
                        onChange={handleChange}
                      />
                      <small className="coa-hint">
                        Next free code. You may change it.
                      </small>
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>
                        Name <i className="fa fa-star required"></i>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={inputs.name}
                        onChange={handleChange}
                        placeholder="Account name"
                      />
                    </div>
                  </div>

                  <div className="row mg-t-18">
                    <div className="col-lg-4">
                      <label>
                        Account Type <i className="fa fa-star required"></i>
                      </label>
                      <select
                        className="form-control"
                        name="account_type"
                        value={inputs.account_type}
                        onChange={handleChange}
                      >
                        <option value="">Select Account Type</option>
                        {accountTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <small className="coa-hint">
                        Which financial statement this account reports into.
                      </small>
                    </div>
                    <div className="col-lg-8 mg-t-10 mg-lg-t-0">
                      <label>Remarks</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="1"
                        value={inputs.description}
                        onChange={handleChange}
                        placeholder="What this account is used for"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="coa-panel">
                <div className="coa-panel-head">
                  <span>
                    <i className="fa fa-balance-scale me-2"></i>Opening Balance
                  </span>
                  <span className="coa-head-note">
                    Only needed when carrying a balance forward
                  </span>
                </div>
                <div className="coa-panel-body">
                  <div className="row">
                    <div className="col-lg-4">
                      <label>Opening Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control coa-num"
                        name="opening_amount"
                        value={inputs.opening_amount}
                        onChange={handleChange}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>Trial Balance Type</label>
                      <select
                        className="form-control"
                        name="trial_balance_dr_cr"
                        value={inputs.trial_balance_dr_cr}
                        onChange={handleChange}
                      >
                        <option value="">Select a Option</option>
                        <option value="dr">Dr — Debit</option>
                        <option value="cr">Cr — Credit</option>
                      </select>
                    </div>
                    <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                      <label>Show in Trial Opening Balance</label>
                      <select
                        className="form-control"
                        name="trial_opening_balance"
                        value={inputs.trial_opening_balance}
                        onChange={handleChange}
                      >
                        <option value="">Select a Option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center">
                <Link
                  to="/chart-of-account"
                  className="btn btn-outline-secondary"
                >
                  Cancel
                </Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={formSubmit}
                  disabled={saving}
                >
                  <i
                    className={`fa ${
                      saving ? "fa-spinner fa-spin" : "fa-send"
                    } mg-r-10`}
                  ></i>
                  {saving ? "Saving..." : "Create Account"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Create;
